import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Service areas data
const serviceAreas = [
  { code: "78023", lat: 29.5778, lng: -98.6900, label: "Helotes" },
  { code: "78108", lat: 29.5700, lng: -98.2200, label: "Cibolo" },
  { code: "78154", lat: 29.5800, lng: -98.3000, label: "Selma / Schertz" },
  { code: "78209", lat: 29.4894, lng: -98.4570, label: "Alamo Heights / Terrell Hills" },
  { code: "78212", lat: 29.4621, lng: -98.4974, label: "Olmos Park / Monte Vista" },
  { code: "78213", lat: 29.5230, lng: -98.5066, label: "Castle Hills / Oak Park" },
  { code: "78216", lat: 29.5400, lng: -98.4800, label: "North Central" },
  { code: "78217", lat: 29.5400, lng: -98.4060, label: "Windcrest" },
  { code: "78230", lat: 29.5450, lng: -98.5400, label: "North Central / Castle Hills" },
  { code: "78231", lat: 29.5900, lng: -98.5200, label: "Hollywood Park" },
  { code: "78232", lat: 29.5800, lng: -98.4700, label: "Thousand Oaks" },
  { code: "78233", lat: 29.5500, lng: -98.3600, label: "Converse / Kirby (N)" },
  { code: "78238", lat: 29.5050, lng: -98.6090, label: "Leon Valley / NW" },
  { code: "78247", lat: 29.5800, lng: -98.4100, label: "Live Oak / NE" },
  { code: "78248", lat: 29.5700, lng: -98.5300, label: "Hollywood Park / NW" },
  { code: "78249", lat: 29.5750, lng: -98.6080, label: "UTSA / Babcock" },
  { code: "78250", lat: 29.5350, lng: -98.6300, label: "Hunters Pond / NW" },
  { code: "78251", lat: 29.4750, lng: -98.6400, label: "Westover Hills" },
  { code: "78254", lat: 29.5450, lng: -98.7200, label: "Alamo Ranch" },
  { code: "78255", lat: 29.6500, lng: -98.6300, label: "Shavano Park / NW" },
  { code: "78256", lat: 29.6080, lng: -98.6360, label: "The Dominion area" },
  { code: "78257", lat: 29.6600, lng: -98.6900, label: "Cordillera Ranch / Dominion" },
  { code: "78258", lat: 29.6400, lng: -98.4900, label: "Stone Oak" },
  { code: "78259", lat: 29.6300, lng: -98.4300, label: "Encino Park" },
  { code: "78260", lat: 29.6700, lng: -98.4500, label: "Timberwood Park" },
  { code: "78266", lat: 29.6500, lng: -98.3000, label: "Garden Ridge" },
];

// Custom marker icon with teal color
const createCustomIcon = (zipCode) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: linear-gradient(135deg, #00C9FF, #92FE9D);
        width: 40px;
        height: 40px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0, 201, 255, 0.4);
      ">
        <span style="
          transform: rotate(45deg);
          color: #2D4156;
          font-weight: 700;
          font-size: 10px;
          font-family: 'Outfit', sans-serif;
        ">${zipCode}</span>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -35],
  });
};

// Component to fit bounds
const FitBounds = () => {
  const map = useMap();
  
  useEffect(() => {
    const bounds = serviceAreas.map(area => [area.lat, area.lng]);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map]);
  
  return null;
};

export const ServiceMap = () => {
  const center = [29.57, -98.50]; // San Antonio center
  
  return (
    <div data-testid="service-map-container" className="relative">
      <MapContainer
        center={center}
        zoom={11}
        scrollWheelZoom={false}
        className="rounded-2xl shadow-xl"
        style={{ height: '450px', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <FitBounds />
        
        {serviceAreas.map((area) => (
          <div key={area.code}>
            <Circle
              center={[area.lat, area.lng]}
              radius={3000}
              pathOptions={{
                color: '#00C9FF',
                fillColor: '#00C9FF',
                fillOpacity: 0.15,
                weight: 2,
              }}
            />
            <Marker
              position={[area.lat, area.lng]}
              icon={createCustomIcon(area.code)}
            >
              <Popup>
                <div className="text-center p-2">
                  <p className="font-outfit font-bold text-navy text-lg mb-1">{area.code}</p>
                  <p className="text-text-light text-sm">{area.label}</p>
                  <p className="text-teal-start font-medium text-xs mt-1">Service Available</p>
                </div>
              </Popup>
            </Marker>
          </div>
        ))}
      </MapContainer>
    </div>
  );
};

export default ServiceMap;
